(() => {
  if (window.CoverlyCartUndo) return;
  const hosts = new Set();
  const entries = new Map();
  const lifetime = 12000;
  let sequence = 0;
  let changes = 0;
  const render = () => hosts.forEach(host => host.render());
  const expire = entry => {
    clearTimeout(entry.timer);
    entry.timer = setTimeout(() => {
      if (entry.busy || [...hosts].some(host => host.matches(':hover') || host.contains(document.activeElement))) {
        expire(entry);
        return;
      }
      entries.delete(entry.id);
      render();
    }, lifetime);
  };
  const sections = () => [
    { id: 'main-cart-items', section: document.getElementById('main-cart-items')?.dataset.id, selector: '.js-contents' },
    { id: 'main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset.id, selector: '.js-contents' },
    { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' }
  ].filter(section => section.section && document.getElementById(section.id));
  const refresh = async (state, requested) => {
    const response = await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error('Cart unavailable');
    const cart = await response.json();
    let markup = state.sections || {};
    if (requested.some(section => typeof markup[section.section] !== 'string')) {
      const url = new URL(routes.cart_url, location.origin);
      url.searchParams.set('sections', requested.map(section => section.section).join(','));
      const fallback = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
      if (!fallback.ok) throw new Error('Cart sections unavailable');
      markup = await fallback.json();
    }
    const replacements = requested.map(section => {
      const html = markup[section.section];
      const content = typeof html === 'string' && new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (!content) throw new Error('Cart section missing');
      return { section, content };
    });
    document.querySelectorAll('cart-items, cart-drawer, #main-cart-footer').forEach(node => node.classList.toggle('is-empty', cart.item_count === 0));
    replacements.forEach(({ section, content }) => {
      if (section.id === 'CartDrawer') {
        document.querySelector('cart-drawer').renderContents({ sections: markup }, false);
      } else {
        const host = document.getElementById(section.id);
        if (host) (host.querySelector(section.selector) || host).innerHTML = content.innerHTML;
      }
    });
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };
  const undo = {
    restoring: false,
    beginChange() { changes++; hosts.forEach(host => host.syncButtons()); },
    endChange() { changes = Math.max(0, changes - 1); hosts.forEach(host => host.syncButtons()); },
    async capture(item) {
      const key = item?.dataset.coverlyKey;
      if (!key) throw new Error('Cart item unavailable');
      const response = await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      const original = cart.items.find(line => line.key === key);
      if (!original) throw new Error('Cart item changed');
      const restore = { id: original.variant_id, quantity: original.quantity, properties: original.properties || {} };
      if (original.selling_plan_allocation) restore.selling_plan = original.selling_plan_allocation.selling_plan.id;
      return { key, title: item.querySelector('.cart-item__name')?.textContent.trim() || '', restore };
    },
    offer(snapshot) {
      const entry = { ...snapshot, id: String(++sequence), busy: false, state: 'removed' };
      entries.set(entry.id, entry);
      expire(entry);
      render();
    },
    async restore(id) {
      const entry = entries.get(id);
      if (!entry || entry.busy || this.restoring || changes || entry.state !== 'removed') return;
      this.restoring = true;
      entry.busy = true;
      clearTimeout(entry.timer);
      document.body.classList.add('coverly-cart-restoring');
      render();
      const requested = sections();
      let confirmed = false;
      try {
        const response = await fetch(routes.cart_add_url, {
          ...fetchConfig(),
          signal: AbortSignal.timeout(15000),
          body: JSON.stringify({ items: [entry.restore], sections: requested.map(section => section.section), sections_url: location.pathname })
        });
        if (!response.ok) {
          entry.state = 'failed';
          return;
        }
        confirmed = true;
        const state = await response.json();
        await refresh(state, requested);
        entries.delete(id);
      } catch {
        entry.state = confirmed ? 'restored' : 'uncertain';
      } finally {
        entry.busy = false;
        this.restoring = false;
        document.body.classList.remove('coverly-cart-restoring');
        if (entries.has(id)) expire(entry);
        render();
      }
    }
  };
  window.CoverlyCartUndo = undo;
  class CoverlyCartUndo extends HTMLElement {
    connectedCallback() { hosts.add(this); this.render(); }
    disconnectedCallback() { hosts.delete(this); }
    syncButtons() {
      this.querySelectorAll('button').forEach(button => { button.disabled = undo.restoring || changes > 0; });
    }
    render() {
      this.hidden = entries.size === 0;
      this.replaceChildren();
      entries.forEach(entry => {
        const row = document.createElement('div');
        row.className = 'coverly-cart-undo__notice';
        const message = document.createElement('span');
        message.setAttribute('role', 'status');
        message.textContent = this.dataset[entry.state] || this.dataset.removed;
        row.append(message);
        const separator = document.createElement('span');
        separator.textContent = ' · ';
        separator.setAttribute('aria-hidden', 'true');
        row.append(separator);
        if (['restored', 'uncertain', 'failed'].includes(entry.state)) {
          const link = document.createElement('a');
          link.href = routes.cart_url;
          link.textContent = this.dataset.viewCart;
          row.append(link);
        } else {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = entry.busy ? this.dataset.restoring : this.dataset.undo;
          button.setAttribute('aria-label', `${button.textContent}: ${entry.title}`);
          button.addEventListener('click', () => undo.restore(entry.id));
          row.append(button);
        }
        this.append(row);
      });
      this.syncButtons();
    }
  }
  customElements.define('coverly-cart-undo', CoverlyCartUndo);
})();
